import { Action } from '../../../../typescript/actionTypes';
import { Filter } from '../../../../typescript/sentInvoiceTypes';
import { StoreSentInvoice } from '../../../../typescript/storeTypes';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import ApiHelper from '../../../../helpers/apiHelper';
import DateHelper from '../../../../helpers/dateHelper';
import FormatHelper from '../../../../helpers/formatHelper';
import InfiniteScroll from '@amreraky/react-infinite-scroller';

import React from 'react';
import TextInput from '../../../../common/TextInput';

type Props = {
    supplierId: number,
    sentInvoices: StoreSentInvoice[],
    invoiceDateRangeStart: string,
    invoiceDateRangeEnd: string,

    getSentInvoices(supplierId: number, page: number, invoiceDateRangeStart: string, invoiceDateRangeEnd: string, filter: Filter, sortBy: string, sortOrder: string): Action,
    getInvoice(invoiceId: number, invoiceNumber: string, addRelatedDocuments: boolean): Action,
    clearSentInvoices(): Action
};

export type SentInvoiceRef = {
  callResetSentInvoices: () => void;
};

export const SentInvoice = forwardRef<SentInvoiceRef, Props>((props: Props, ref) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState('invoicedate');
    const [sortOrder, setSortOrder] = useState('desc');
    const [filter, setFilter] = useState<Filter>({
        supplier: '',
        invoicenumber: '',
        customer: '',
        amount: '',
        paid: ''
    });

    useImperativeHandle(ref, () => ({
        callResetSentInvoices
    }));

    const callResetSentInvoices = (): void => {
        props.clearSentInvoices();
        setCurrentPage(1);

        if(props.supplierId === 0 || props.invoiceDateRangeStart === null || props.invoiceDateRangeEnd === null) 
            return;

        props.getSentInvoices(props.supplierId, 1, props.invoiceDateRangeStart, props.invoiceDateRangeEnd, filter, sortBy, sortOrder);
    };

    const fetchSentInvoices = (): void => {
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        
        props.getSentInvoices(props.supplierId, nextPage, props.invoiceDateRangeStart, props.invoiceDateRangeEnd, filter, sortBy, sortOrder);
    };

    const changeFilteringDebounce: () => void = useMemo(() => ApiHelper.debounce(() => changeFiltering(), 500), [filter]);
    const changeFiltering = (): void => {
        localStorage.setItem('sentInvoice_Filter', JSON.stringify(filter));

        callResetSentInvoices();
    };

    const changeSorting = (newSortBy: string) => {
        let newSortOrder = sortOrder;
        if(sortBy === newSortBy) {
            newSortOrder = sortOrder === 'desc' ? 'asc' : 'desc';
        } else {
            newSortOrder = 'asc';
        }
        localStorage.setItem('sentInvoice_SortBy', newSortBy);
        localStorage.setItem('sentInvoice_SortOrder', newSortOrder);

        props.clearSentInvoices();
        setCurrentPage(1);
        setSortOrder(newSortOrder);
        setSortBy(newSortBy);

        props.getSentInvoices(props.supplierId, 1, props.invoiceDateRangeStart, props.invoiceDateRangeEnd, filter, sortBy, sortOrder);
    };

    const renderHeaderReadonlyItem = (name: string, title: string) => {
        return (
            <div className={`sent-invoice__list-header__${name}`}>
                <i className={`fas ${sortBy !== name ? '' : sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'}`}  onClick={() => changeSorting(name)}/>
                <div id={`js-sent-invoice__list-header__${name}__title`} className={`sent-invoice__list-header__${name}__title`} onClick={() => changeSorting(name)}>{title}</div>
            </div>
        );
    };

    const renderFilterPlaceholder = (name: string) => {
        return (
            <div className={`sent-invoice__list-filter__${name}`}/>
        );
    };

    const renderFilterTextBox = (name: string) => {
        return (
            <div className={`sent-invoice__list-filter__${name}`}>
                <div className={`sent-invoice__list-filter__${name}__input`}>
                    <TextInput search name={`filter__${name}`} value={filter[name]} onChange={(event: React.SyntheticEvent<HTMLInputElement> & { currentTarget: HTMLInputElement }) => {
                        const newFilter = {...filter};
                        newFilter[name] = event.currentTarget.value;
                        setFilter(newFilter);
                        changeFilteringDebounce();
                    }}/>
                </div>
            </div>
        );
    };

    const renderFilterCheckbox = (name: string) => {
        return (
            <div className={`sent-invoice__list-filter__${name}`}>
                <div className={`sent-invoice__list-filter__${name}__input`}>
                    <div className="" onClick={() => {
                            const newFilter = {...filter};
                            if(newFilter[name] === '1')
                            {
                                newFilter[name] = '0';
                            } else if(newFilter[name] === '0') {
                                newFilter[name] = '';
                            } else {
                                newFilter[name] = '1';
                            }
                            setFilter(newFilter);
                            changeFilteringDebounce();
                        }} >{filter[name] === '' ? 'ALL' :
                            (filter[name] === '1' ?
                                <i className="fas fa-check"/> :
                                <i className="fas fa-times"/> )
                        }</div>
                    </div>
                </div>
        );
    };

    const sortInvoices = (): StoreSentInvoice[] => {
        const sortedInvoices = props.sentInvoices.slice();

        sortedInvoices.sort((i1,i2) => i1.currentCount - i2.currentCount);

        return sortedInvoices;
    };

    const hasMoreSentInvoices = (): boolean => {
        if(props.sentInvoices.length === 0) return false;

        const currentCount = Math.max(...props.sentInvoices.map(i => i.currentCount));
        const totalCount = props.sentInvoices[0].totalCount;

        return currentCount < totalCount;
    };

    useEffect(() => { //Did Mount
        const localStorageItem = localStorage.getItem('sentInvoice_Filter');
        const storageFilter =  JSON.parse(localStorageItem as string);
        if(storageFilter && storageFilter !== filter) {
            setFilter(storageFilter);
        }

        const storageSortBy =  localStorage.getItem('sentInvoice_SortBy');
        if(storageSortBy && storageSortBy !== sortBy) {
            setSortBy(storageSortBy);
        }

        const storageSortOrder =  localStorage.getItem('sentInvoice_SortOrder');
        if(storageSortOrder && storageSortOrder !== sortOrder) {
            setSortOrder(storageSortOrder);
        }
    }, []);

    useEffect(() => { //unmount
      return () => {
        props.clearSentInvoices();
      };
    }, []);

    return (
        <div className="sent-invoice">
            <React.Fragment>
                {props.supplierId > 0 ?
                <React.Fragment>
                    <div className="sent-invoice__list-item sent-invoice__list-item--fixed">
                        {renderHeaderReadonlyItem('invoicedate', 'Invoice Date')}
                        {renderHeaderReadonlyItem('supplier', 'Supplier')}
                        {renderHeaderReadonlyItem('invoicenumber', 'Invoice Number')}
                        {renderHeaderReadonlyItem('customer', 'Customer')}
                        {renderHeaderReadonlyItem('amount', 'Amount')}
                        {renderHeaderReadonlyItem('paid', 'Paid')}
                    </div>
                    <div className="sent-invoice__list-filter">
                        {renderFilterPlaceholder('invoicedate')}
                        {renderFilterTextBox('supplier')}
                        {renderFilterTextBox('invoicenumber')}
                        {renderFilterTextBox('customer')}
                        {renderFilterTextBox('amount')}
                        {renderFilterCheckbox('paid')}
                    </div>
                </React.Fragment> : null}
                {props.sentInvoices.length > 0 ?
                    <div className="sent-invoice__scrollbarcontainer">
                        <InfiniteScroll
                            id="infini-sent-invoice"
                            loadMore={fetchSentInvoices}
                            loader={<div key="sent-invoice-loading" className="sent-invoice__loading"><div className="fas fa-circle-notch fa-spin"/></div>}
                            hasMore={hasMoreSentInvoices()}
                            pageStart={1}
                            initialLoad={false}
                            useWindow={false}
                        >
                            {sortInvoices().map(i =>
                                <div key={i.invoiceId} id={`${i.invoiceId}`} onDoubleClick={() => props.getInvoice(i.invoiceId, i.invoiceNumber, false)} className="sent-invoice__list-item">
                                    <div className="sent-invoice__list-item__invoicedate">{DateHelper.getFormattedDate(i.invoiceDate, '/')}</div>
                                    <div className={`sent-invoice__list-item__supplier ${i.supplierId === props.supplierId ? 'sent-invoice__list-item__owner' : ''}`}>{i.supplier}</div>
                                    <div className="sent-invoice__list-item__invoicenumber">{i.invoiceNumber}</div>
                                    <div className={`sent-invoice__list-item__customer ${i.customerId === props.supplierId ? 'sent-invoice__list-item__owner' : ''}`}>{i.customer}</div>
                                    <div className="sent-invoice__list-item__amount">{FormatHelper.getFormatted2DecimalNumber(i.amount)}€</div>
                                    <div className="sent-invoice__list-item__paid">{i.paid ? <i className="fas fa-check" aria-hidden="true" /> : <i className="fas fa-times" aria-hidden="true" />}</div>
                                    <div className="sent-invoice__list-item__download"
                                        onClick={() => props.getInvoice(i.invoiceId, i.invoiceNumber, false)}>
                                            <i className="fas fa-cloud-download-alt fa-2x" />
                                    </div>
                                    <div className="sent-invoice__list-item__download_all">
                                        <div className="download-swap">
                                            <img className="download_all" src="/images/download-with-attachments.png"/>
                                            <img className="download_all-hovered" src="/images/download-with-attachments-hovered.png"
                                                onClick={() => props.getInvoice(i.invoiceId, i.invoiceNumber, true)}/>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </InfiniteScroll>
                    </div>
                : null }
            </React.Fragment>
        </div>
    );
});
SentInvoice.displayName = 'SentInvoice';
